<script lang="ts">
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { shopify } from '$lib/shopify';
  import { cn } from '$lib/utils';
  import { wishlist } from '$lib/wishlist';
  import Button from './ui/button.svelte';
  import Icon from './ui/icon/icon.svelte';
  import { CurrencyCode, LanguageCode } from '$gql/storefront/schemas';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';
  import { lang } from '$lib/lang';
  import { region, type Region } from '$lib/region';
  import { user } from '$lib/stores/user';
  import { createMutation } from '@tanstack/svelte-query';
  import type { GetCustomerQuery } from '$gql/storefront/operations';
  import AlertCard from './alert-card.svelte';
  import { t } from '$lib/i18n';
  import { DEFAULT_TITLE } from '$lib/constants';

  export let productId: string;
  export let productsIdList: string[] | undefined = undefined;
  export let size: 'xs' | 'sm' | 'lg' = 'sm';
  export let variant: 'button' | 'icon' | 'filled-icon' | 'all' = 'button';
  export let selectedVariantId: string | undefined = undefined;
  export let productVariantName: string | undefined = undefined;
  let className: string | undefined = undefined;
  export { className as class };

  $: inWishlist = ($wishlist ?? []).includes(productId);
  $: allProductsInWishlist = productsIdList?.every((p) => ($wishlist ?? []).includes(p));

  const getProductData = async () => {
    const data = await $shopify.getRegionalizedProductPriceById({
      id: productId,
      lang: $lang.toUpperCase() as LanguageCode,
      country: parseCountryAsCode($optimalUSCountry)
    });

    return data.product;
  };

  const klaviyoWishlist = createMutation<
    unknown,
    unknown,
    {
      action: 'add-to-wishlist' | 'remove-from-wishlist';
      user: GetCustomerQuery['customer'];
      region: Region;
      price: string;
      currency: CurrencyCode;
      product: string;
      wishlist: string[];
    }
  >({
    mutationKey: ['klaviyoWishlist'],
    mutationFn: async (vars) => {
      const res = await fetch('/api/klaviyo/create-event', {
        method: 'POST',
        body: JSON.stringify(vars)
      });

      if (!res.ok) throw new Error('Error sending Klaviyo Wishlist event');

      return res.json();
    }
  });

  const analyticsHandle = async (type: 'add' | 'remove') => {
    const product = await getProductData();

    if (!product) return;

    const productVariant = product.variants.nodes.find((item) => item.id === selectedVariantId);
    productVariantName = productVariant?.title || '';

    if (!productVariantName) {
      const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
      productVariantName =
        availableVariant?.title && availableVariant?.title !== DEFAULT_TITLE
          ? availableVariant.title
          : product.title;
    }

    analyticsEvent({
      event:
        type === 'add' ? AnalyticsEvents.ADD_TO_WISHLIST : AnalyticsEvents.REMOVE_FROM_WISHLIST,
      currency: product?.priceRange.minVariantPrice.currencyCode || CurrencyCode.Usd,
      value: +product?.priceRange.minVariantPrice.amount,
      items: [
        {
          item_id: product.handle,
          item_name: product?.title || '',
          item_category: product?.productType || '',
          item_variant: productVariantName,
          item_sku: productVariant?.sku
        }
      ]
    });

    $klaviyoWishlist.mutate({
      action: type === 'add' ? 'add-to-wishlist' : 'remove-from-wishlist',
      user: $user
        ? { ...$user, wishlist: { id: $user.wishlist?.id as string, value: productId } }
        : $user,
      region: $region,
      price: product?.priceRange.minVariantPrice.amount,
      currency: product?.priceRange.minVariantPrice.currencyCode || CurrencyCode.Usd,
      product: product?.title || '',
      wishlist: $wishlist
    });
  };
</script>

{#if variant == 'all'}
  {#if allProductsInWishlist}
    <AlertCard
      icon="check-circle"
      message={$t('wishlist.button.multiple_products.added')}
      class="mt-5"
      size="md"
    />
  {:else}
    <Button
      variant={$user ? 'accent' : 'ghost'}
      icon={'heart-empty'}
      class="mt-5"
      size="sm"
      on:click={() => {
        wishlist.toggleMultiple(productsIdList || []);
      }}
      >{$t('wishlist.button.multiple_products.add')}
    </Button>
  {/if}
{:else if variant === 'button'}
  <Button
    name={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    class={cn('shrink-0', className)}
    icon={inWishlist ? 'heart-filled' : 'heart-empty'}
    size="sm"
    variant="ghost"
    on:click={() => {
      analyticsHandle(inWishlist ? 'remove' : 'add');
      wishlist.toggle(productId);
    }}
  />
{:else if variant === 'filled-icon'}
  <button
    aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    type="button"
    class={cn(
      'outline-none transition motion-reduce:transition-none p-1.5 lg:p-2 focus-visible:ring rounded-sm border-none opacity-80 hover:opacity-100',
      className
    )}
    on:click={(e) => {
      e.preventDefault();
      analyticsHandle(inWishlist ? 'remove' : 'add');
      wishlist.toggle(productId);
    }}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.54267 6.12186L9.54308 6.12232L9.54328 6.12256L9.54358 6.12291L9.54362 6.12295L9.5438 6.12316L9.9999 6.65946L10.456 6.12316L10.4562 6.12295L10.4562 6.12291L10.4565 6.12256L10.4567 6.12232L10.4571 6.12186L10.4655 6.11233C10.4738 6.103 10.4874 6.08789 10.5061 6.06781C10.5436 6.02761 10.6011 5.9678 10.6769 5.89489C10.8291 5.74853 11.0514 5.55246 11.3286 5.35694C11.8918 4.95958 12.6305 4.6 13.4396 4.6C14.7183 4.6 15.496 5.12662 15.9327 5.86724C16.3897 6.64242 16.5171 7.73187 16.2906 8.88428C15.7894 11.4343 13.8072 14.1636 10.4197 16.0927L10.4197 16.0927C10.418 16.0936 10.4164 16.0945 10.4148 16.0955C10.1568 16.2423 10.1245 16.254 10.0821 16.2629C10.0403 16.2716 9.95951 16.2716 9.91767 16.2629C9.87503 16.2539 9.84263 16.2422 9.58014 16.0927C6.19246 14.1635 4.21004 11.4344 3.70925 8.88437C3.48293 7.73199 3.61047 6.6425 4.0676 5.86726C4.50436 5.12657 5.28204 4.6 6.5602 4.6C7.3693 4.6 8.10801 4.95958 8.67125 5.35694C8.94839 5.55246 9.17073 5.74853 9.32288 5.89489C9.39866 5.9678 9.45624 6.02761 9.49368 6.06781C9.51238 6.08789 9.526 6.103 9.53429 6.11233L9.54267 6.12186Z"
        stroke-width="1.2"
        class={cn(
          'stroke-primary fill-highlight/88 motion-safe:transition',
          inWishlist && 'fill-primary'
        )}
      />
    </svg>
  </button>
{:else}
  <button
    aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    type="button"
    class={cn(
      'outline-none transition motion-reduce:transition-none focus-visible:ring p-1 rounded-sm border-none',
      className
    )}
    on:click={async () => {
      analyticsHandle(inWishlist ? 'remove' : 'add');
      wishlist.toggle(productId);
    }}
  >
    <Icon icon={inWishlist ? 'heart-filled' : 'heart-empty'} size={size === 'xs' ? 'md' : 'lg'} />
  </button>
{/if}
